import { useMutation, useQueryClient } from "@tanstack/react-query";

import { fetchJson, postForm, postJson, postMultipart } from "./client";
import { queryKeys } from "./queries";
import type {
  CreateOdometerInput,
  CreateOdometerOk,
  CreateEntryInput,
  CreateEntryOk,
  DeleteHistoryItem,
  EntryDetail,
  FuelImportOk,
  UpdateEntryInput,
} from "./types";

export function useUpdateEntry(entryId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateEntryInput) =>
      fetchJson<{ ok: true; entry: EntryDetail }>(`/api/entries/${entryId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.entry(entryId) });
      queryClient.invalidateQueries({ queryKey: ["overview"] });
      queryClient.invalidateQueries({ queryKey: ["history"] });
      queryClient.invalidateQueries({ queryKey: ["analysis"] });
      queryClient.invalidateQueries({ queryKey: ["cars"] });
    },
  });
}

export function useDeleteEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entryId: number) =>
      fetchJson<{ ok: true }>(`/api/entries/${entryId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["overview"] });
      queryClient.invalidateQueries({ queryKey: ["history"] });
      queryClient.invalidateQueries({ queryKey: ["analysis"] });
      queryClient.invalidateQueries({ queryKey: ["cars"] });
    },
  });
}

export function useDeleteHistoryEntries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entries: DeleteHistoryItem[]) =>
      fetchJson<{ ok: true; deleted: number }>("/api/history/batch-delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entries }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["overview"] });
      queryClient.invalidateQueries({ queryKey: ["history"] });
      queryClient.invalidateQueries({ queryKey: ["analysis"] });
      queryClient.invalidateQueries({ queryKey: ["cars"] });
      queryClient.invalidateQueries({ queryKey: ["odometer"] });
    },
  });
}

/**
 * Create a fuel entry. On success, invalidates the overview query so the
 * new row appears without a page reload.
 */
export function useCreateEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEntryInput) => {
      const { car_id, ...fields } = input;
      const query = car_id ? `?car_id=${encodeURIComponent(car_id)}` : "";
      return postForm<CreateEntryOk>(`/api/entries${query}`, { ...fields });
    },
    onSuccess: () => {
      // Invalidate every variant of the overview key (any history_limit).
      queryClient.invalidateQueries({ queryKey: ["overview"] });
      queryClient.invalidateQueries({ queryKey: ["history"] });
      queryClient.invalidateQueries({ queryKey: ["analysis"] });
      queryClient.invalidateQueries({ queryKey: ["cars"] });
      queryClient.invalidateQueries({ queryKey: ["fuel-stations"] });
    },
  });
}

export function useCreateOdometer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOdometerInput) => {
      const query = input.car_id ? `?car_id=${encodeURIComponent(input.car_id)}` : "";
      return postJson<CreateOdometerOk>(`/api/odometer${query}`, {
        odometer_km: input.odometer_km,
        entry_date: input.entry_date,
        notes: "",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["odometer"] });
      queryClient.invalidateQueries({ queryKey: ["history"] });
      queryClient.invalidateQueries({ queryKey: ["overview"] });
      queryClient.invalidateQueries({ queryKey: ["analysis"] });
      queryClient.invalidateQueries({ queryKey: ["cars"] });
    },
  });
}

export interface FuelImportInput {
  carId: number;
  fuelCsv: File;
  odometerCsv?: File | null;
}

function fuelImportForm(input: FuelImportInput): FormData {
  const form = new FormData();
  form.append("car_id", String(input.carId));
  form.append("fuel_csv", input.fuelCsv);
  if (input.odometerCsv) form.append("odometer_csv", input.odometerCsv);
  return form;
}

export function useValidateFuelImport() {
  return useMutation({
    mutationFn: (input: FuelImportInput) =>
      postMultipart<FuelImportOk>(
        "/api/import/fuel-records/validate",
        fuelImportForm(input),
      ),
  });
}

export function useFuelImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: FuelImportInput) =>
      postMultipart<FuelImportOk>(
        "/api/import/fuel-records",
        fuelImportForm(input),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["overview"] });
      queryClient.invalidateQueries({ queryKey: ["history"] });
      queryClient.invalidateQueries({ queryKey: ["analysis"] });
      queryClient.invalidateQueries({ queryKey: ["cars"] });
    },
  });
}
